import { ApiError } from '@/services/api';
import { get as getIntegrationsPublicApiKey } from '@/services/api/resources/integrations-public-api-key/actions';
import emitter from '@/services/emitter';
import { integrationsService } from '@/services/integrations/integrationsService';
import { multiStepConfigService } from '@/services/integrations/multiStepConfigService';
import prismaticClient from '@/services/integrations/prismaticClient';
import {
  ConfigVariable,
  InputInstanceConfigVariable,
  Instance,
  IntegrationDetailed,
  RequiredConfigVariable,
} from '@/services/integrations/types';
import * as E from 'fp-ts/Either';

class IntegrationsConfigService {
  private readonly PUBLIC_API_KEY_VARIABLE = 'Public API Key';
  private readonly POWER_BI_CONNECTION_KEY = 'Microsoft Power BI Connection';
  private PUBLIC_API_KEY: string | undefined;

  public isSingleStepConfiguration(integration: IntegrationDetailed) {
    const configVariables = this.getVisibleConfigVariables(integration);
    return configVariables.length === 1
      && configVariables[0].dataType === 'CONNECTION'
      && configVariables[0].connection.oauth2Type === 'AUTHORIZATION_CODE';
  }

  /**
   * Single Step Configuration is a configuration that requires only one config variable.
   * @param integration
   */
  public getSingleConfigVariable(integration: IntegrationDetailed) {
    const visibleConfigVariables = this.getVisibleConfigVariables(integration);
    return visibleConfigVariables[0];
  }

  public prepareMultipleSteps(integration: IntegrationDetailed) {
    const configVariables = this.getVisibleConfigVariables(integration);

    return multiStepConfigService.formatSteps(integration, configVariables);
  }

  public async createInstance(integration: IntegrationDetailed): Promise<Instance> {
    const defaultVariables = await this.createDefaultConfigVariables(integration);
    const instance = await prismaticClient.createInstance(
      integration.latestVersion.id,
      this.makeInstanceName(integration),
      defaultVariables,
    );
    integrationsService.onInstanceCreated(instance);

    return instance;
  }

  public getAuthorizeUrl(instance: Instance) {
    const configVariable = instance.configVariables.nodes.find(v => v.authorizeUrl);
    if (!configVariable) {
      return null;
    }

    if (this.isPowerBiConnection(configVariable)) {
      return this.makePowerBiAuthorizeUrl(configVariable.authorizeUrl!);
    }

    return configVariable.authorizeUrl;
  }

  /**
   * Once Instance is fully configured we need to deploy it. After that the Integration will be activated.
   * If there are previously deployed Instances we need to delete them.
   */
  public async deployInstance(instance: Instance, integration: IntegrationDetailed): Promise<void> {
    await integrationsService.destroyAllInstances(integration, instance.id);

    const deployedInstance = await prismaticClient.deployInstance(instance.id);
    integration.instances.nodes = [deployedInstance];
    emitter.emit('integrationUpdated', integration);

    integrationsService.onInstanceDeployed(instance);
  }

  /**
   * This method checks whether the Customer has authenticated within 3rd party app opened by authorizeUrl.
   * @param instance
   */
  public async checkSingleStepConnection(instance: Instance): Promise<boolean> {
    const instanceConnectionStatus = await prismaticClient.getInstanceConnectionStatus(instance.id);
    const connectionStatus = instanceConnectionStatus
      .configVariables
      .nodes
      .find(v => v.requiredConfigVariable.dataType === 'CONNECTION')
      ?.status;

    return connectionStatus === 'ACTIVE';
  }

  public async getConnectionAccessToken(instance: Instance): Promise<string | null> {
    const instanceConfig = await prismaticClient.getInstanceConfigVariablesMeta(instance.id);
    const meta = instanceConfig
      .configVariables
      .nodes
      .find(v => v.requiredConfigVariable.dataType === 'CONNECTION')
      ?.meta;

    if (!meta) {
      return null;
    }

    return JSON.parse(meta).token?.access_token;
  }

  private makeInstanceName(integration: IntegrationDetailed) {
    return `${integration.name} - ${new Date().toISOString()}`;
  }

  private getVisibleConfigVariables(integration: IntegrationDetailed) {
    return integration.requiredConfigVariables.nodes.filter(
      configVariable => this.isVisibleConfigVariable(configVariable),
    );
  }

  private isVisibleConfigVariable(configVariable: RequiredConfigVariable) {
    if (configVariable.meta === null) {
      return true;
    }

    const meta = JSON.parse(configVariable.meta);
    return meta.visibleToCustomerDeployer;
  }

  private isPowerBiConnection(configVariable: ConfigVariable) {
    return configVariable.requiredConfigVariable.key === this.POWER_BI_CONNECTION_KEY;
  }

  private makePowerBiAuthorizeUrl(authorizeUrl: string) {
    return `${authorizeUrl}&prompt=select_account`;
  }

  /**
   * Fulfil the Integration Public API Key if it is present among required config variables.
   */
  private async createDefaultConfigVariables(integration: IntegrationDetailed): Promise<InputInstanceConfigVariable[]> {
    const requiresPublicApiKey = integration
      .requiredConfigVariables
      .nodes
      .find(configVariable => configVariable.key === this.PUBLIC_API_KEY_VARIABLE);

    if (requiresPublicApiKey) {
      return [{
        key: this.PUBLIC_API_KEY_VARIABLE,
        value: await this.getPublicApiKey(),
      }];
    }

    return [];
  }

  private async getPublicApiKey(): Promise<string> {
    if (!this.PUBLIC_API_KEY) {
      const resultEither = await getIntegrationsPublicApiKey()();
      this.PUBLIC_API_KEY = E.getOrElse<ApiError, {public_api_key: string}>(error => {
        throw error;
      })(resultEither).public_api_key;
    }

    return this.PUBLIC_API_KEY;
  }
}

export const integrationsConfigService = new IntegrationsConfigService();
